if (!customElements.get('phnx-google-reviews')) {
  class PhnxGoogleReviews extends HTMLElement {
    constructor() {
      super();
      this.currentPage = 0;
      this.pageCount = 1;
      this.pageWidth = 0;
      this.autoplayTimer = null;
      this.scrollRaf = null;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-pgr-viewport]');
      this.track = this.querySelector('.phnx-google-reviews__track');
      this.slides = Array.from(this.querySelectorAll('.phnx-google-reviews__slide'));
      this.nav = this.querySelector('[data-pgr-nav]');
      this.dotsContainer = this.querySelector('[data-pgr-dots]');
      this.prevButton = this.querySelector('[data-pgr-prev]');
      this.nextButton = this.querySelector('[data-pgr-next]');

      if (!this.viewport || this.slides.length === 0) return;

      let options = {};
      try {
        options = JSON.parse(this.getAttribute('data-carousel-options') || '{}');
      } catch (error) {
        options = {};
      }

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = Boolean(options.autoplay) && !this.reducedMotion;
      this.autoplayInterval = options.autoplayInterval || 5000;

      if (this.prevButton) this.prevButton.addEventListener('click', () => this.goTo(this.currentPage - 1, true));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.goTo(this.currentPage + 1, true));

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      this.update();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.onResize);
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    }

    onResize() {
      this.update();
    }

    onScroll() {
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = requestAnimationFrame(() => {
        this.setCurrentPage(this.readPageFromScroll());
      });
    }

    /*
     * A "page" is one viewport's worth of slides. Measured from the rendered slide width rather
     * than scrollWidth / clientWidth, because the flex gaps would otherwise inflate the count.
     */
    update() {
      const clientWidth = this.viewport.clientWidth;
      const slideWidth = this.slides[0].getBoundingClientRect().width;
      if (!clientWidth || !slideWidth) return;

      const gap = this.track ? parseFloat(getComputedStyle(this.track).columnGap) || 0 : 0;
      const perView = Math.max(1, Math.round((clientWidth + gap) / (slideWidth + gap)));

      this.pageWidth = perView * (slideWidth + gap);
      this.pageCount = Math.max(1, Math.ceil(this.slides.length / perView));
      this.buildDots();

      if (this.nav) this.nav.hidden = this.pageCount <= 1;

      // A resize can turn a static row into a carousel (or back), so re-evaluate autoplay.
      if (this.pageCount <= 1) this.stopAutoplay();
      else if (!this.autoplayTimer) this.startAutoplay();

      this.setCurrentPage(this.readPageFromScroll());
    }

    readPageFromScroll() {
      const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
      // The last page is usually a partial scroll, so snap the tail end to it explicitly.
      if (scrollLeft + clientWidth >= scrollWidth - 2) return this.pageCount - 1;
      if (!this.pageWidth) return 0;
      return Math.min(this.pageCount - 1, Math.max(0, Math.round(scrollLeft / this.pageWidth)));
    }

    setCurrentPage(page) {
      this.currentPage = page;
      if (!this.dotsContainer) return;

      Array.from(this.dotsContainer.children).forEach((dot, index) => {
        const isActive = index === page;
        dot.classList.toggle('phnx-google-reviews__dot--active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    buildDots() {
      if (!this.dotsContainer) return;
      if (this.dotsContainer.children.length === this.pageCount) return;

      this.dotsContainer.innerHTML = '';
      if (this.pageCount <= 1) return;

      for (let index = 0; index < this.pageCount; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'phnx-google-reviews__dot';
        dot.setAttribute('aria-label', `Go to review page ${index + 1} of ${this.pageCount}`);
        dot.addEventListener('click', () => this.goTo(index, true));
        this.dotsContainer.appendChild(dot);
      }
    }

    /* Wraps around at both ends so arrows and autoplay loop through the reviews. */
    goTo(page, userInitiated) {
      if (this.pageCount <= 1) return;

      let target = page;
      if (target < 0) target = this.pageCount - 1;
      if (target > this.pageCount - 1) target = 0;

      this.viewport.scrollTo({
        left: target * this.pageWidth,
        behavior: this.reducedMotion ? 'auto' : 'smooth',
      });

      this.setCurrentPage(target);
      if (userInitiated) this.stopAutoplay();
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplay || this.pageCount <= 1) return;

      this.autoplayTimer = setInterval(() => {
        this.goTo(this.currentPage + 1, false);
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define('phnx-google-reviews', PhnxGoogleReviews);
}
